import React, { memo, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Group,
  Paper,
  Skeleton,
  Table,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { EpAlert } from "../Components/EpMessage";
import { Td, TdText, Th, ThText } from "../Components/EpTable";
import { TxDisplay } from "../Components/TxDisplay";
import { AmountDisplay } from "../Components/AmountDisplay";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import EpPagination from "../Components/EpPagination";
import { isEmpty, useIsSmallScreen } from "../Components/Utils";
import Widget from "../Components/Widget";
import { TokenIcons } from "../Components/EpTxType";
import Icon from "../Components/Icon";
import { TokenEmpty, TokenIcon } from "../Components/EpIcon";
import getChainIcon from "./utils/getChainIcon";
import { ContractDisplay } from "../Components/ContractDisplay";
import { use4byteSignQuery } from "./hooks/use4byteSignQuery";
import { useTxTagsQuery } from "./hooks/useTxTagsQuery";
import { stringifyUrl } from "query-string";
import RelatedTableAffix from "./TxRelatedTable/RelatedTableAffix";
import { useScrollIntoView } from "@mantine/hooks";
import { useQuery } from "react-query";
import { TxReceiptParams, TxReceiptResult } from "../Transaction/api/type";
import { CopyAddress } from "../Components/CopyAddress";
import { useRequest } from "ahooks";
import { TransactionTags } from "./api/type";

dayjs.extend(utc);

export const transferMethod = (mehtod: string) => {
  return mehtod.replace(
    /^[a-z]|([A-Z]+)(?=[A-Z])|[A-Z][a-z]/g,
    function (str, offset) {
      if (offset === 0) {
        return str.toUpperCase();
      } else {
        return " " + str.charAt(0).toUpperCase() + str.slice(1);
      }
    }
  );
};

const getBlockFromStroage = async (chain: string, blockNumber: number) => {
  const storage = getStorage();
  const gsReference = ref(
    storage,
    `gs://eigenphi-blockchain-raw-${chain}/${blockNumber}`
  );

  const txuri = await getDownloadURL(gsReference);

  const odata = await (await fetch(txuri)).json();

  return odata;
};

// 获取flashbots bundle数据
const useFlashbotsBundleQuery = (chain: string, blockNumber: number) => {
  const [data, setData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setData([]);
    setIsLoading(false);
  }, [chain, blockNumber]);

  useEffect(() => {
    if (chain === "ethereum") {
      let isMounted = true;
      setIsLoading(true);

      (async () => {
        try {
          const path = `https://${
            process.env.REACT_APP_ENV === "test" ? "test-" : ""
          }eigentx-related-txs-bundle-tepu46la6a-uw.a.run.app`;
          const endpoint = stringifyUrl({
            url: path,
            query: { block_number: blockNumber },
          });

          const data = await (await fetch(endpoint)).json();

          if (isMounted) {
            setData(data);
          }
        } finally {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      })();

      return () => {
        isMounted = false;
      };
    }
  }, [chain, blockNumber]);

  return {
    data,
    isLoading,
  };
};

const PositionItem = memo(
  ({
    position,
    bundleIndex,
    dark,
  }: {
    position: number;
    bundleIndex: number;
    dark: boolean;
  }) => {
    const options = [
      {
        color: "#8950FC",
        background: dark ? "rgba(137,80,252,0.20)" : "#EEE5FF",
        hideArrow: true,
      },
      {
        color: "#F64E60",
        background: dark ? "rgba(246,78,96,0.20)" : "#FFE2E5",
      },
      {
        color: "#0BB783",
        background: dark ? "rgba(11,183,131,0.20)" : "#D7F9EF",
      },
      {
        color: "#009CFA",
        background: dark ? "rgba(0,156,250,0.20)" : "#E1F0FF",
      },
      {
        color: "#FFA800",
        background: dark ? "rgba(255,168,0,0.20)" : "#FFF4DE",
      },
      {
        color: "#B0DC00",
        background: dark ? "rgba(176,220,0,0.20)" : "#F5FFCE",
      },
      {
        color: "#7E8299",
        background: dark ? "rgba(126,130,153,0.20)" : "#F3F6F9",
      },
    ] as any;

    const curOps = options[bundleIndex % (options.length - 1)];

    return (
      <Group
        noWrap
        spacing={0}
        position="center"
        sx={{
          width: 32,
          height: 32,
          background: curOps?.background,
          borderRadius: 6,
        }}
      >
        <Text
          sx={{
            color: curOps?.color,
            fontFamily: "Consolas, Consolas-Regular",
            fontSize: 14,
          }}
        >
          {position}
        </Text>
      </Group>
    );
  }
);

export const Protocols = memo(
  ({
    lps,
  }: {
    lps: Array<{
      name: string;
      symbol: string;
      protocol: {
        id: number;
        icon: string;
      };
      tokens: Array<{
        symbol: string;
        address: string;
      }>;
    }>;
  }) => {
    return (
      <Group spacing={2} noWrap>
        {lps?.map((lp, idx) => {
          if (!lp?.protocol?.icon) return <TokenEmpty key={idx} size={16} />;

          return (
            <Tooltip
              key={idx}
              withArrow
              label={`${lp?.symbol}:${lp?.tokens
                ?.map((token) => token.symbol)
                .join("-")}`}
            >
              <Box sx={{ lineHeight: 1 }}>
                <TokenIcon
                  src={
                    lp?.protocol?.id
                      ? `https://storage.googleapis.com/eigenphi-token-icons/protocols/${lp?.protocol?.id}.png`
                      : lp?.protocol?.icon
                          ?.replace(
                            "https://eigenphi.io/images/tokens/protocols/",
                            "https://storage.googleapis.com/eigenphi-token-icons/protocols/"
                          )
                          ?.replace(
                            "/images/tokens/protocols/",
                            "https://storage.googleapis.com/eigenphi-token-icons/protocols/"
                          )
                  }
                  alt=""
                  size={16}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Group>
    );
  }
);

function getLabels(transactionTags: TransactionTags): string {
  const isSandwich = Boolean(transactionTags?.sandwichTag);
  const isJIT = Boolean(transactionTags?.jitTag);

  let labels = transactionTags?.types?.[0];
  if (isSandwich && transactionTags?.sandwichTag?.role?.length > 0) {
    const role =
      transactionTags?.sandwichTag?.role?.[1] ||
      transactionTags?.sandwichTag?.role?.[0];
    labels =
      `Sandwich ` +
      role?.charAt(0)?.toUpperCase() +
      role?.slice(1).toLowerCase();

    // 去掉MiddleAttacker label
    if (labels === "middleAttacker") {
      if (transactionTags?.sandwichTag?.role?.[0] === "victim") {
        const role = transactionTags?.sandwichTag?.role?.[0];
        labels =
          `Sandwich ` +
          role?.charAt(0)?.toUpperCase() +
          role?.slice(1).toLowerCase();
      } else {
        labels = "";
      }
    }

    if (labels.toLocaleLowerCase() === "midfrontrun") {
      labels = `Sandwich ` + "FrontRun&BackRun";
    }
  }
  if (isJIT && transactionTags?.jitTag?.role?.length > 0) {
    const role =
      transactionTags?.jitTag?.role?.[1] || transactionTags?.jitTag?.role?.[0];
    labels =
      `JIT ` + role?.charAt(0)?.toUpperCase() + role?.slice(1).toLowerCase();
  }
  return labels;
}

const TxBlockTable = memo(function ({
  chain,
  blockNumber,
  txhash,
  position,
  isMultiTx,
  isActive,
}: {
  chain: {
    id: number;
    name: string;
    etherscanName: string;
    etherscanUrlPrefix: string;
  };
  blockNumber: number;
  txhash: string;
  position: number;
  isMultiTx?: boolean;
  isActive: boolean;
}) {
  const txhashArr = txhash?.split(",");
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const isSmallScreen = useIsSmallScreen();
  const theme = useMantineTheme();
  const { scrollIntoView, targetRef } = useScrollIntoView<HTMLDivElement>({
    offset: 60,
  });

  const dark = theme.colorScheme === "dark";

  const chainId = chain.name.toLowerCase();

  const { data, loading: isLoading } = useRequest(
    () => getBlockFromStroage(chainId, blockNumber),
    {
      ready: isActive,
      refreshDeps: [chainId, blockNumber],
    }
  );

  const { transactions = [] } = data || {};

  // 获取flashbots bundle数据
  const { data: flashbotsData, isLoading: flashbotsLoading } =
    useFlashbotsBundleQuery(chainId, blockNumber!);
  const bundleTxs = flashbotsData?.blocks?.[0]?.transactions;

  // 获取tokens，lps，labels等数据
  const { data: txTagsData, isLoading: txTagsLoading } = useTxTagsQuery({
    chain: chain.id,
    txhash: transactions
      .filter((item: any) => !Boolean(item?.stackFrameList?.[0]?.error))
      .map((item: any) => item.transactionHash)
      .join(","),
  });

  const { data: signData, isLoading: signLoading } = use4byteSignQuery({
    chain: chain.id,
    inputs: transactions
      .filter((item: any) => !Boolean(item?.functionName))
      .map((item: any) => item.input.slice(0, 10))
      .join(","),
  });

  useEffect(() => {
    if (position) {
      setPageNum(Math.ceil((position + 1) / pageSize));
    }
    //eslint-disable-next-line
  }, [position]);
  const { data: txReceiptData } = useQuery<TxReceiptResult, TxReceiptParams>(
    [
      "fetchTxReceipt",
      {
        txHash: txhashArr[0],
      },
    ],
    {
      refetchOnMount: false,
      retry: false,
      enabled: Boolean(txhashArr[0]) && chainId === "ethereum",
    }
  );

  if (!isLoading && !transactions?.length) {
    return <EpAlert height={120}>No Data Found!</EpAlert>;
  }

  const colcount = 10;

  const pages = transactions.slice(
    (pageNum - 1) * pageSize,
    pageNum * pageSize
  );

  const hasCurrentTxhash = pages.some(
    (item: any) => txhashArr[0] === item?.transactionHash
  );

  const miner = txReceiptData?.block?.miner || "";

  const rows =
    isLoading || flashbotsLoading
      ? Array.from({ length: 10 }).map((_, idx) => (
          <tr key={"TLB" + idx}>
            {Array.from({ length: colcount }).map((_, idx) => (
              <Td key={`skeleton-column-${idx}`}>
                <Skeleton width="100%" height={30} />
              </Td>
            ))}
          </tr>
        ))
      : pages?.map((item: any, idx: number) => {
          const { tokens, lps, transactionTags, minertips } =
            txTagsData?.[item.transactionHash] || {};

          let method;
          if (item?.functionName) {
            let functionName = item?.functionName?.split("(")[0];
            method = transferMethod(functionName);
          } else if (item?.input) {
            method = item.input.slice(0, 10);
            if (method === "0x") method = "Transfer";
            if (signData[method]?.count) {
              const text_signature = signData[method].results[0].text_signature;
              method = transferMethod(text_signature?.split("(")[0]);
            }
          } else {
            method = "";
          }

          const isSandwich = Boolean(transactionTags?.sandwichTag);
          const isJIT = Boolean(transactionTags?.jitTag);

          let labels = getLabels(transactionTags);

          let lpArr = lps;
          if (isSandwich) {
            lpArr = [transactionTags?.sandwichTag?.commonTrader?.lp] as any;
          }

          const gasFeeWei = (item.gasUsed || 0) * (item.gasPrice || 0);
          const minerTipWei =
            Number(minertips) > 0 ? Number(minertips) - gasFeeWei : 0;

          const isCurrentTx = txhash.includes(item.transactionHash);

          const bundleTx =
            bundleTxs?.find(
              (bundle: any) => bundle?.transaction_hash === item.transactionHash
            ) || {};

          return (
            <React.Fragment key={idx}>
              {!isEmpty(bundleTx) && bundleTx?.tx_index === 0 ? (
                <tr>
                  <Td colSpan={colcount}>
                    <Group spacing={10}>
                      <TdText>Bundle Index: {bundleTx?.bundle_index}</TdText>
                      <TdText>/</TdText>
                      <TdText>Bundle Type: {bundleTx?.bundle_type}</TdText>
                    </Group>
                  </Td>
                </tr>
              ) : null}
              <tr
                style={{
                  backgroundColor: isCurrentTx ? "rgba(255,200,0,0.10)" : "",
                }}
              >
                <Td>
                  {txhashArr[0] === item.transactionHash && isActive ? (
                    <Box ref={targetRef}>{isActive}</Box>
                  ) : null}
                  <PositionItem
                    dark={dark}
                    bundleIndex={bundleTx?.bundle_index}
                    position={item.transactionIndex}
                  />
                </Td>
                <Td>
                  <Group noWrap spacing={4}>
                    {isCurrentTx ? (
                      <Tooltip label="Current Tx" position="top" withArrow>
                        <Box>
                          <Icon
                            name="icon-bx-current-location"
                            color="#009CFA"
                            size={18}
                          />
                        </Box>
                      </Tooltip>
                    ) : null}
                    <TxDisplay
                      tx={item.transactionHash}
                      chain={chainId}
                      isError={Boolean(item?.stackFrameList?.[0]?.error)}
                      position="right"
                      eigentx={!(labels?.length > 0)}
                      targetBlank
                    />
                  </Group>
                </Td>
                <Td>
                  {signLoading ? (
                    <Skeleton width="100%" animate={false} height={30} />
                  ) : method ? (
                    <Box
                      sx={{
                        padding: "2px 5px",
                        minWidth: 70,
                        display: "inline-block",
                        borderRadius: 4,
                        background: dark ? "#20313F" : "#E9F4FD",
                        color: dark ? "#B5B5C3" : "#464E5F",
                        fontWeight: 400,
                        textAlign: "center",
                      }}
                    >
                      <Text
                        truncate
                        size="xs"
                        sx={{ whiteSpace: "nowrap", maxWidth: "10vw" }}
                      >
                        {method}
                      </Text>
                    </Box>
                  ) : (
                    <TdText>-</TdText>
                  )}
                </Td>
                <Td>
                  {txTagsLoading ? (
                    <Skeleton width="100%" animate={false} height={30} />
                  ) : labels?.length > 0 ? (
                    <TdText>
                      {labels?.charAt(0)?.toUpperCase() + labels?.slice(1)}
                    </TdText>
                  ) : (
                    <TdText>-</TdText>
                  )}
                </Td>
                <Td>
                  {txTagsLoading ? (
                    <Skeleton width="100%" animate={false} height={30} />
                  ) : tokens?.length! > 0 ? (
                    <Box sx={{ minWidth: 100 }}>
                      <TokenIcons
                        tokens={tokens as any}
                        chain={chainId}
                        dark={dark}
                        maxCount={8}
                      />
                    </Box>
                  ) : (
                    <TdText>-</TdText>
                  )}
                </Td>
                <Td>
                  {txTagsLoading ? (
                    <Skeleton width="100%" animate={false} height={30} />
                  ) : lpArr?.length > 0 ? (
                    <Group noWrap spacing={6}>
                      <Badge
                        size="sm"
                        radius={4}
                        sx={{
                          padding: "0 4px",
                          background: dark
                            ? "rgba(255,255,255,0.06)"
                            : "#E9ECEF",
                        }}
                      >
                        <Group spacing={2} noWrap>
                          <Icon
                            name="icon-location"
                            size={12}
                            color={dark ? "#868E96" : "#B5B5C3"}
                          />
                          <TdText sx={{ color: dark ? "#B5B5C3" : "#868E96" }}>
                            {lpArr.length}
                          </TdText>
                        </Group>
                      </Badge>
                      <Protocols lps={lpArr as any} />
                    </Group>
                  ) : (
                    <TdText>-</TdText>
                  )}
                </Td>
                <Td>
                  <ContractDisplay
                    address={item.fromAddress}
                    link={`/eigentx/${item.transactionHash}?tab=from`}
                  />
                </Td>
                <Td>
                  <ContractDisplay
                    address={item.toAddress}
                    link={`/eigentx/${item.transactionHash}?tab=to`}
                  />
                </Td>
                <Td align="right">
                  <AmountDisplay value={gasFeeWei / 1e18} digits={8} />
                </Td>

                {chainId === "bsc" ? null : (
                  <Td align="right">
                    {txTagsLoading ? (
                      <Skeleton width="100%" animate={false} height={30} />
                    ) : minerTipWei ? (
                      <AmountDisplay value={minerTipWei / 1e18} digits={8} />
                    ) : (
                      <TdText>-</TdText>
                    )}
                  </Td>
                )}
              </tr>
            </React.Fragment>
          );
        });

  const chainIcon = getChainIcon({
    dark,
    chain,
    size: 32,
    showChainName: true,
  });

  return (
    <>
      {isActive ? (
        <style>{`
        .eigenphi_backtop_button{display:none}
      `}</style>
      ) : null}
      <Group
        sx={{
          padding: isSmallScreen ? "0px 10px" : "0px 24px",
        }}
        spacing={10}
      >
        <Tooltip label={chainIcon?.label}>{chainIcon?.icon}</Tooltip>
        <Group noWrap spacing={10}>
          <Text
            size={14}
            sx={{
              fontWeight: 700,
              color: dark ? "#FFFFFF" : "#25262B",
            }}
          >
            Block Height {blockNumber}
          </Text>
        </Group>
        {data ? (
          <Group noWrap spacing={10}>
            <Text
              size={14}
              sx={{
                color: dark ? "#FFFFFF" : "#25262B",
              }}
            >
              {data?.transaction_count} transactions
            </Text>
          </Group>
        ) : null}
        {data ? (
          <Group noWrap spacing={10}>
            <Text
              size={14}
              sx={{
                color: dark ? "#FFFFFF" : "#25262B",
              }}
            >
              at UTC{" "}
              {dayjs
                .utc(data?.block_timestamp * 1000)
                .format("YYYY-MM-DD HH:mm:ss")}
            </Text>
          </Group>
        ) : null}
        {data ? (
          <Group noWrap spacing={10}>
            <Text
              size={14}
              sx={{
                color: dark ? "#FFFFFF" : "#25262B",
              }}
            >
              {txReceiptData?.minerTag ? (
                <Group noWrap spacing={10}>
                  by
                  <a
                    href={`https://etherscan.io/address/${miner}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      textDecoration: "none",
                    }}
                  >
                    {txReceiptData?.minerTag}
                    <Icon name="icon-openlink" size={12} />
                  </a>
                  <Text>(Hex: {txReceiptData?.block?.extraData})</Text>
                </Group>
              ) : miner ? (
                <Group noWrap spacing={0}>
                  <ContractDisplay
                    fullAddress={!isSmallScreen}
                    address={miner}
                    chain={chainId!}
                    hideCopy
                    external
                  />
                  <CopyAddress text={miner} color="#54B498" />
                </Group>
              ) : null}
            </Text>
          </Group>
        ) : null}
      </Group>
      <Box sx={{ padding: isSmallScreen ? 0 : "0px 24px 24px 24px" }}>
        <Widget
          title={
            <Group spacing={4}>
              <Text sx={{ fontWeight: 400 }}>
                {isMultiTx ? "First" : "Current"} trading position:
              </Text>
              <Text color="#009CFA">{position}</Text>
            </Group>
          }
          hideFooter
        >
          <Paper sx={{ overflowX: "auto" }}>
            <Table highlightOnHover horizontalSpacing={20}>
              <thead>
                <tr>
                  <Th>
                    <ThText>Position</ThText>
                  </Th>
                  <Th>
                    <ThText>Txn Hash</ThText>
                  </Th>
                  <Th>
                    <ThText>Method</ThText>
                  </Th>
                  <Th>
                    <ThText>Labels</ThText>
                  </Th>
                  <Th>
                    <ThText>Tokens</ThText>
                  </Th>
                  <Th>
                    <ThText>LPs</ThText>
                  </Th>
                  <Th>
                    <ThText>From</ThText>
                  </Th>
                  <Th>
                    <ThText>To</ThText>
                  </Th>
                  <Th style={{ textAlign: "right" }}>
                    <ThText>GasFee({chainId === "bsc" ? "BNB" : "ETH"})</ThText>
                  </Th>
                  {chainId === "bsc" ? null : (
                    <Th style={{ textAlign: "right" }}>
                      <ThText>Builder Payments</ThText>
                    </Th>
                  )}
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </Table>
          </Paper>
          <Group
            position="right"
            align={isSmallScreen ? "flex-start" : "center"}
            sx={(theme) => ({
              width: "100%",
              padding: "16px 20px 19px",
              borderTop:
                theme.colorScheme === "dark"
                  ? `1px solid ${theme.colors.dark[6]}`
                  : `1px solid ${theme.colors.gray[2]}`,
              flexDirection: isSmallScreen ? "column-reverse" : "row",
            })}
            noWrap
          >
            {isLoading ? (
              <Skeleton width="200px" height={24} />
            ) : (
              <EpPagination
                onPrev={() => {
                  setPageNum((n) => {
                    return n - 1;
                  });
                }}
                onNext={() => {
                  setPageNum((n) => {
                    return n + 1;
                  });
                }}
                isLastPage={
                  pageNum === Math.ceil(transactions.length / pageSize)
                }
                pageNum={pageNum}
                pageSize={pageSize}
                onPageSizeChange={(s: number) => {
                  setPageNum(1);
                  setPageSize(s);
                }}
              />
            )}
          </Group>
        </Widget>
        {isActive ? (
          <RelatedTableAffix
            disabledCurrentBtn={!hasCurrentTxhash}
            onCurrentClick={() => scrollIntoView({ alignment: "center" })}
          />
        ) : null}
      </Box>
    </>
  );
});

export default TxBlockTable;
